/* eslint-disable @typescript-eslint/member-ordering */
import { DatePipe } from '@angular/common';
import { ID } from '@datorama/akita';
import { Coordinate } from 'ol/coordinate';
import { Observable } from 'rxjs';

import { Column, Entity, OneToMany } from '../entity/index';
import { IEntity } from '../interfaces/models';
import { ColumnType, LanguagePage, PageTitles } from '../interfaces/types';
import { WalklistService } from '../services/walklist';
import { VendorGroup } from './vendorGroup.model';
import { IWalklist } from './walklist.model';
import { commonConstants } from '../constants/common.constants';

export interface ITerritory extends IEntity {
  territoryName: string;
  territoryType: string;
  territoryActive: boolean;
  territoryGeom: any;
  territoryDescription: string;
  territoryStatus: string;
  vendorGroup: string;
  orgId: string;
  outletId: string;
  created: string;
  modified: string;
  territoryAddressCount: number;
  territoryFor: string;
  grids: any[];
  vendorGroupId: string;
  vendorAccountName: string;
  walklists?: Observable<IWalklist[]>;
  vendorNotes?: string;
  appliedFilters?: any;
  assignedDate?: Date;
  assignedVendorDate?: string;
  addressIds?: any;
  progress?: any;
}

@Entity({
  name: PageTitles.territory,
  language: LanguagePage.territory,
})
export class Territory implements ITerritory {
  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 2,
    },
  })
  territoryName: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 2,
      hide: false,
    },
    form: {
      defaultPosition: 1,
      hide: false,
    },
  })
  territoryType: string;

  @Column({
    type: ColumnType.boolean,
    isEditable: true,
    table: {
      defaultPosition: 3,
    },
    form: {
      defaultPosition: 2,
    },
  })
  territoryActive: boolean;

  @Column({
    type: ColumnType.string,
    isEditable: true,
    table: {
      defaultPosition: 7,
      hide: true,
    },
    form: {
      defaultPosition: 3,
    },
  })
  territoryDescription: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 8,
      hide: true,
    },
    form: {
      defaultPosition: 4,
    },
  })
  territoryFor: string;

  @Column({
    type: ColumnType.number,
    form: {
      defaultPosition: 5,
    },
  })
  territoryAddressCount: number;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 5,
    },
    form: {
      defaultPosition: 6,
    },
  })
  vendorGroup: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 4,
    },
  })
  outletId: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 5,
    },
  })
  orgId: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 6,
    },
  })
  createdBy: string;

  @Column({
    type: ColumnType.date,
    table: {
      defaultPosition: 7,
    },
  })
  createdAt: Date;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 9,
      hide: true,
    },
  })
  modifiedBy: string;

  @Column({
    type: ColumnType.date,
    table: {
      defaultPosition: 10,
      hide: true,
    },
  })
  modifiedAt: Date;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 4,
    },
  })
  territoryStatus: string;

  @Column({
    type: ColumnType.date,
    table: {
      defaultPosition: 11,
      hide: true,
    },
  })
  assignedDate: Date;

  @Column({
    type: ColumnType.string,
    form: {
      defaultPosition: 7,
    },
  })
  get created(): string {
    const datepipe: DatePipe = new DatePipe('en-CA');
    const date = datepipe.transform(
      this.createdAt,
      commonConstants.DATE_FORMATS.MMMdd_YYYY_HHmm_a
    );

    return `${this.createdBy}, ${date.toString()}`;
  }

  @Column({
    type: ColumnType.string,
    form: {
      defaultPosition: 8,
    },
  })
  get modified(): string {
    const datepipe: DatePipe = new DatePipe('en-CA');
    const date = datepipe.transform(
      this.createdAt,
      commonConstants.DATE_FORMATS.MMMdd_YYYY_HHmm_a
    );

    return this.modifiedBy ? `${this.modifiedBy}, ${date.toString()}` : '';
  }

  @Column({
    type: ColumnType.string,
    form: {
      defaultPosition: 12,
    },
  })
  get assignedVendorDate(): string {
    const datepipe: DatePipe = new DatePipe('en-CA');
    if (this.assignedDate) {
      const date = datepipe.transform(
        this.assignedDate,
        commonConstants.DATE_FORMATS.MMMdd_YYYY_HHmm_a
      );
      return `${date.toString()}`;
    } else {
      return '';
    }
  }

  @OneToMany({
    repository: WalklistService,
    expression: { territoryId: (i: Territory) => i.id },
  })
  walklists: Observable<IWalklist[]>;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 1,
    },
  })
  id: ID;
  territoryGeom: any;

  vendorGroupId: string;
  vendorAccountName: string;
  grids: any[];
  vendorNotes: string;
  appliedFilters: any;

  href?: string;

  addressIds: any;
  progress?: any;

  constructor(
    territory: ITerritory = {
      id: '',
      territoryName: '',
      territoryType: '',
      territoryActive: false,
      territoryGeom: [],
      territoryDescription: '',
      vendorGroup: '',
      outletId: '',
      orgId: '',
      createdBy: '',
      createdAt: new Date(),
      modifiedBy: '',
      modifiedAt: new Date(),
      created: '',
      modified: '',
      territoryAddressCount: 0,
      territoryFor: '',
      vendorGroupId: '',
      vendorAccountName: '',
      grids: [],
      territoryStatus: '',
      vendorNotes: '',
      appliedFilters: {},
      assignedDate: null,
      assignedVendorDate: '',
      addressIds: {},
      progress: 0,
    }
  ) {
    const {
      id,
      territoryName,
      territoryType,
      territoryActive,
      territoryGeom,
      territoryDescription,
      vendorGroup,
      outletId,
      orgId,
      createdBy,
      createdAt,
      modifiedBy,
      modifiedAt,
      territoryAddressCount,
      territoryFor,
      vendorGroupId,
      vendorAccountName,
      grids,
      territoryStatus,
      vendorNotes,
      appliedFilters,
      assignedDate,
      addressIds,
      progress,
    } = territory;

    this.id = id;
    this.territoryName = territoryName;
    this.territoryType = territoryType;
    this.territoryActive = territoryActive;
    this.territoryGeom = territoryGeom;
    this.territoryDescription = territoryDescription;
    this.vendorGroup = vendorGroup;
    this.outletId = outletId;
    this.orgId = orgId;
    this.createdBy = createdBy;
    this.createdAt = createdAt;
    this.modifiedBy = modifiedBy;
    this.modifiedAt = modifiedAt;
    this.territoryAddressCount = territoryAddressCount;
    this.territoryFor = territoryFor;
    this.vendorGroupId = vendorGroupId;
    this.vendorAccountName = vendorAccountName;
    this.grids = grids;
    this.territoryStatus = territoryStatus;
    this.vendorNotes = vendorNotes;
    this.appliedFilters = appliedFilters;
    this.assignedDate = assignedDate;
    this.addressIds = addressIds;
    this.progress = progress;
  }

  static createFromDTO(record: ITerritory): Territory {
    return new Territory({
      ...record,
      vendorGroupId: record.vendorGroup,
    });
  }

  static completeTerritoryInformation(
    item: Territory,
    federationNames: VendorGroup[] = [],
    vendorNames = []
  ) {
    const created = federationNames.filter(
      (federation) => federation.id === item.createdBy
    );
    const modified = federationNames.filter(
      (federation) => federation.id === item.modifiedBy
    );
    const vendorGroup = vendorNames.filter(
      (vendor) => vendor.Name === item.vendorGroupId
    );

    item.vendorGroup = vendorGroup.length > 0 ? vendorGroup[0].Name : '';
    item.modifiedBy =
      modified.length > 0 ? modified[0].vendorName : item.modifiedBy;
    item.createdBy =
      created.length > 0 ? created[0].vendorName : item.createdBy;
    return item;
  }
}
